import { useRef, useState } from "react"
import { Button } from "@/components/ui/button"
import { Check } from "lucide-react"
import { landingCopy } from "../landingCopy"
import { primaryButtonClass } from "../lightTheme"
import { useLandingLanguage } from "../useLandingLanguage"
import ButtonHalo from "./ButtonHalo.jsx"
import mark from "./mark.png"
const userPath =
  "M9.42 9.25a6.3 6.3 0 0 1 5.76 3.69l.07.15v3.16h-14v-3.16l.07-.15a6.3 6.3 0 0 1 5.76-3.69zm-2.34 1.5a4.8 4.8 0 0 0-4.33 2.68v1.32h11v-1.32a4.8 4.8 0 0 0-4.33-2.68zM8.25 0c1.8 0 3.25 1.46 3.25 3.25v.5C11.5 5.55 10.04 7 8.25 7h-.5A3.25 3.25 0 0 1 4.5 3.75v-.5C4.5 1.45 5.96 0 7.75 0zm-.5 1.5C6.78 1.5 6 2.28 6 3.25v.5c0 .97.78 1.75 1.75 1.75h.5c.97 0 1.75-.78 1.75-1.75v-.5c0-.97-.78-1.75-1.75-1.75z"
const edgeLine =
  "bg-[linear-gradient(to_bottom,#A1A1A1_0%,#171717_25%,#171717_75%,#A1A1A1_100%)]"

function VerificationDiagram() {
  const language = useLandingLanguage()
  const forgeProof = landingCopy(language, "forgeProof")

  return (
    <div className="relative flex w-full items-center overflow-hidden">
      <div className="relative z-[1] flex size-[72px] shrink-0 items-center justify-center rounded-[12px] border border-[rgba(0,0,0,0.08)] bg-[#FAFAFA] md:size-24">
        <svg viewBox="0 0 16 16" className="size-7 text-[#171717]" aria-hidden="true">
          <path fill="currentColor" d={userPath} />
        </svg>
        <span className={`absolute top-1/2 -right-px h-14 w-px -translate-y-1/2 ${edgeLine}`} />
      </div>
      <div className="relative z-0 -ml-px flex min-w-0 flex-1 items-center self-stretch pr-2.5">
        <span className="relative block h-px w-full bg-[#171717]">
          <span className="absolute top-1/2 -right-1 h-0 w-0 -translate-y-1/2 border-y-[5px] border-l-[10px] border-y-transparent border-l-[#171717]" />
        </span>
        <div className="absolute inset-0 z-[1] flex items-center justify-center">
          <div className="flex size-16 items-center justify-center rounded-[12px] border border-[#171717] bg-[#FAFAFA]">
            <img src={mark} alt="" className="size-10" />
          </div>
        </div>
      </div>
      <div className="relative z-[1] flex h-60 min-w-0 flex-1 items-center rounded-[12px] border border-[rgba(0,0,0,0.08)] bg-[#FAFAFA] p-4 md:aspect-square md:h-[344px] md:max-w-[360px] md:flex-none">
        <span className="absolute left-2.5 top-2.5 flex items-center gap-1" aria-hidden="true">
          <span className="size-1.5 rounded-full bg-[#D4D4D4]" />
          <span className="size-1.5 rounded-full bg-[#D4D4D4]" />
          <span className="size-1.5 rounded-full bg-[#D4D4D4]" />
        </span>
        <div className="flex flex-col gap-2">
          {forgeProof.windowLines.map((line) => (
            <p
              key={line}
              className="pointer-events-none select-none whitespace-nowrap text-[12px] font-normal leading-4 text-[#171717] [font-family:ui-monospace,SFMono-Regular,Menlo,monospace]"
            >
              {line}
            </p>
          ))}
        </div>
        <span className={`absolute top-1/2 -left-px h-14 w-px -translate-y-1/2 ${edgeLine}`} />
      </div>
    </div>
  )
}

export default function ForgeProofSection() {
  const language = useLandingLanguage()
  const forgeProof = landingCopy(language, "forgeProof")
  const buttonRef = useRef(null)
  const [buttonLit, setButtonLit] = useState(false)

  return (
    <section className="ml-[calc(50vw-50%-1.5rem)] grid min-w-0 w-full grid-cols-1 items-center gap-x-6 gap-y-10 overflow-x-clip lg:grid-cols-12">
      <div className="order-1 min-w-0 lg:order-none lg:col-span-6 lg:col-start-7 lg:row-start-1">
        <VerificationDiagram />
      </div>
      <div className="order-2 flex min-w-0 flex-col gap-8 lg:order-none lg:col-span-6 lg:col-start-1 lg:row-start-1">
        <h2 className="text-balance text-[48px] font-bold leading-[56px] tracking-[-0.04em] text-[#171717] max-md:text-[32px] max-md:leading-10">
          {forgeProof.title}
        </h2>
        <p className="text-balance text-[16px] font-normal leading-6 text-[#4D4D4D] lg:text-[18px] lg:leading-7">
          {forgeProof.bodyBefore}
          <code className="rounded border border-[rgba(0,0,0,0.08)] bg-[rgba(0,0,0,0.05)] px-1.5 py-0.5 text-[14px] leading-5 [font-family:ui-monospace,SFMono-Regular,Menlo,monospace]">
            {forgeProof.code}
          </code>
          {forgeProof.bodyAfter}
        </p>
        <div className="flex flex-col gap-4">
          <span ref={buttonRef} className="relative inline-flex w-fit max-w-full">
            <ButtonHalo lit={buttonLit} targetRef={buttonRef} />
            <Button
              type="button"
              className={`${primaryButtonClass} relative z-10 h-auto min-h-10 max-w-full gap-1 whitespace-normal`}
              onMouseEnter={() => setButtonLit(true)}
              onMouseLeave={() => setButtonLit(false)}
            >
              {forgeProof.cta}
              <span aria-hidden="true">↗</span>
            </Button>
          </span>
          <ul className="m-0 flex list-none flex-col gap-2 p-0">
            {forgeProof.notes.map((line) => (
              <li
                key={line}
                className="flex items-start gap-2 text-[14px] font-normal leading-5 text-[#4D4D4D]"
              >
                <Check
                  className="mt-0.5 size-3.5 shrink-0 text-[#171717]"
                  strokeWidth={1.5}
                  aria-hidden="true"
                />
                {line}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  )
}
